import type { OfferPlanKey } from "@/lib/payments/offer-plan";

/**
 * The courses on sale, as /catalog's cards and /pricing's cards both show them. One list so the
 * two pages cannot drift apart: add, remove or rename a course here and both follow.
 */
export type CatalogCourse = {
  /** Anchor for the /catalog card: #course-<id>. */
  id: string;
  title: string;
  tagline: string;
  audience: string;
  level: string;
  focus: readonly string[];
  /** The course's /catalog page (lib/data/public-courses.ts). */
  href: string;
  /** What /api/checkout/offer sells for this course (lib/payments/offer-plan.ts). */
  offerPlanKey: OfferPlanKey;
};

/** One $7 payment buys the course on the card, every level of it. */
export const CATALOG_PRICE = "$7";
/**
 * The facts line under a card's title, e.g. "4 modules · Certificate". The count comes from the
 * course's modules in the database (lib/data/catalog-module-counts.ts), so it can't go stale
 * the way the old fixed "16 modules" did for every course; without one, only the certificate shows.
 */
export function catalogCourseFacts(moduleCount: number | undefined): string {
  return moduleCount ? `${moduleCount} module${moduleCount === 1 ? "" : "s"} · Certificate` : "Certificate";
}

export const CATALOG_COURSES: readonly CatalogCourse[] = [
  {
    id: "ai-essentials",
    title: "AI Essentials",
    tagline: "Build your AI fluency.",
    audience: "Working professionals",
    level: "Beginner → Intermediate",
    // The course's own module names, word for word.
    focus: ["AI Foundations", "Generative AI, Prompting and Trust", "AI for Work and Problem Solving", "Building With AI"],
    href: "/catalog/ai-essentials",
    offerPlanKey: "literacy",
  },
  {
    id: "agentic-ai",
    title: "Agentic AI",
    tagline: "Move from using AI to building with it.",
    audience: "Tech Professionals",
    level: "Intermediate → Advanced",
    // The course's own module names, word for word, as AI Essentials does.
    focus: [
      "What Is an Agent?",
      "How Agents Think and Act",
      "Building a Working Agent",
      "Workflows and Orchestration",
      "Multi-Agent Systems",
      "Reliability, Evaluation, Security, and Governance",
    ],
    href: "/catalog/agentic-ai",
    offerPlanKey: "agentic",
  },
];
